import { useMutation, useLazyQuery, gql } from '@apollo/client'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Slide from '@mui/material/Slide'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { DigitalAssetScope, Query } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useCallback } from 'react'

import { SelectedAssets } from './Downloadables'
import { useCurrentIds } from './useCurrentIds'

const ASSET_LIMIT = 1000

type SelectedAssetsProps = {
  selected: SelectedAssets
  setSelected: React.Dispatch<React.SetStateAction<SelectedAssets>>
}

const SelectedAssetsFooter: React.FC<
  React.PropsWithChildren<SelectedAssetsProps>
> = ({ selected, setSelected }) => {
  const { retailerAccountId } = useCurrentIds()

  const zipResponse = useCallback((data: Query) => {
    const { url } = data.zipDigitalAssets
    const link = document.createElement('a')
    link.href = url || ''
    link.click()
  }, [])

  const [recordDownloads] = useMutation(RECORD_DOWNLOADS)

  const [zipAndDownload] = useLazyQuery<Query>(ZIP_ASSETS, {
    fetchPolicy: 'network-only',
    onCompleted: zipResponse,
  })

  const handleZipDownload = async () => {
    const scope = createZipScope(selected)
    recordDownloads({
      variables: {
        params: {
          resource_id: retailerAccountId,
          resource_type: 'retailer_accounts',
          digital_asset_ids: Object.keys(selected),
        },
      },
    })
    zipAndDownload({
      variables: {
        params: {
          scope,
        },
      },
    })
  }

  const assetsSelected = Object.keys(selected).length

  return (
    <Slide direction="up" in={!!assetsSelected} mountOnEnter unmountOnExit>
      <AppBar position="sticky" style={{ top: 'auto', bottom: 0 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => setSelected({})}
            size="large"
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" style={{ marginLeft: '16px' }}>
            {I18n.t('downloadables.xAssetsSelected', {
              count: assetsSelected,
            })}
          </Typography>
          {assetsSelected >= ASSET_LIMIT && (
            <Box color="white" display="flex" ml={4}>
              <ErrorOutlineIcon />
              <Typography style={{ marginLeft: '8px' }}>
                You are over the limit of 1000 assets, please remove some
                selections
              </Typography>
            </Box>
          )}
          <div style={{ flexGrow: 1 }} />
          <Button
            size="large"
            color="inherit"
            onClick={handleZipDownload}
            variant="outlined"
            disabled={assetsSelected >= ASSET_LIMIT}
            data-testid={'downloadSelectedButton'}
          >
            <strong>{I18n.t('downloadables.downloadSelected')}</strong>
          </Button>
        </Toolbar>
      </AppBar>
    </Slide>
  )
}

const createZipScope = (
  selectedAssets: SelectedAssets,
): DigitalAssetScope[] => {
  const associableIdTable = Object.values(selectedAssets).reduce<
    Record<string, string[]>
  >((associableIds, selectedAsset) => {
    if (selectedAsset.external_video_url) {
      return associableIds
    }
    associableIds[selectedAsset.associable_id] = [
      ...(associableIds[selectedAsset.associable_id] || []),
      selectedAsset.digital_asset_associable_id || '',
    ]
    return associableIds
  }, {})

  return Object.keys(associableIdTable).map((key) => ({
    resource_type: 'brands',
    resource_id: key,
    digital_asset_associable_ids: associableIdTable[key] || [],
  }))
}

const ZIP_ASSETS = gql`
  query zipDigitalAssets($params: ZipDigitalAssetsParams!) {
    zipDigitalAssets(params: $params) {
      url
    }
  }
`
const RECORD_DOWNLOADS = gql`
  mutation recordDownloadDigitalAssets(
    $params: RecordMultipleDownloadDigitalAssetsParams!
  ) {
    recordMultipleDownloadDigitalAssets(params: $params)
  }
`

export default SelectedAssetsFooter
